import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Loader2, Clock, RefreshCw, Bell, BellOff,
  Building2, CheckCircle, PenLine, Landmark, ChevronDown,
  ChevronRight, Timer, Sparkles,
} from "lucide-react";

const TYPE_CONFIG = {
  new_businesses: {
    dot: "bg-sky-500",
    icon: Building2,
    iconColor: "text-sky-600",
    cardBg: "bg-sky-50/60",
    cardBorder: "border-sky-100",
    hoverBorder: "hover:border-sky-300",
    activeBg: "bg-sky-100",
  },
  offer_verified: {
    dot: "bg-emerald-500",
    icon: CheckCircle,
    iconColor: "text-emerald-600",
    cardBg: "bg-emerald-50/60",
    cardBorder: "border-emerald-100",
    hoverBorder: "hover:border-emerald-300",
    activeBg: "bg-emerald-100",
  },
  offer_updated: {
    dot: "bg-amber-500",
    icon: PenLine,
    iconColor: "text-amber-600",
    cardBg: "bg-amber-50/60",
    cardBorder: "border-amber-100",
    hoverBorder: "hover:border-amber-300",
    activeBg: "bg-amber-100",
  },
  new_city_complete: {
    dot: "bg-violet-500",
    icon: Landmark,
    iconColor: "text-violet-600",
    cardBg: "bg-violet-50/60",
    cardBorder: "border-violet-100",
    hoverBorder: "hover:border-violet-300",
    activeBg: "bg-violet-100",
  },
};

const CountdownBadge = ({ days, isExpiring }) => {
  if (days <= 0) return <span className="text-[9px] text-slate-300 font-medium">Expired</span>;
  if (isExpiring) {
    return (
      <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold animate-pulse">
        <Timer className="w-2.5 h-2.5" /> {days}d left
      </span>
    );
  }
  return (
    <span className="text-[9px] text-slate-400 font-medium">{days}d</span>
  );
};

const NewDot = ({ isNew }) => {
  if (!isNew) return null;
  return <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0 animate-pulse" />;
};

export default function WhatsNewPage() {
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const [comingSoon, setComingSoon] = useState([]);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [csLoading, setCsLoading] = useState(false);
  const scrollRef = useRef(null);

  const fetchFeed = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/whats-new`);
      setFeed(res.data.items);
      setLastUpdated(res.data.last_updated);
      setNewCount(res.data.new_count || 0);
    } catch { /* silent */ }
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // Mark as read when page is visited
  useEffect(() => {
    if (!loading && feed && feed.length > 0) {
      const timer = setTimeout(() => {
        axios.post(`${API}/whats-new/mark-read`).catch(() => {});
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, feed]);

  // Restore scroll position
  // Removed — now handled globally by <ScrollRestore /> in App.js.

  const fetchComingSoon = async () => {
    if (comingSoon.length > 0) { setShowComingSoon(!showComingSoon); return; }
    setCsLoading(true);
    try {
      const res = await axios.get(`${API}/whats-new/coming-soon`);
      setComingSoon(res.data.features || []);
      setShowComingSoon(true);
    } catch {}
    finally { setCsLoading(false); }
  };

  const toggleOptIn = async (featureId, currentState) => {
    setComingSoon(prev => prev.map(f =>
      f.feature_id === featureId ? { ...f, opted_in: !currentState } : f
    ));
    try {
      await axios.post(`${API}/whats-new/coming-soon/opt-in`, {
        feature_id: featureId,
        opt_in: !currentState,
      });
    } catch {
      setComingSoon(prev => prev.map(f =>
        f.feature_id === featureId ? { ...f, opted_in: currentState } : f
      ));
    }
  };

  const handleTap = (action) => {
    if (!action) return;
    if (action.type === "business" && action.business_id) {
      navigate(`/offer/${action.business_id}`);
    } else if (action.type === "city" && action.city) {
      navigate(`/city/${encodeURIComponent(action.city)}`);
    } else {
      navigate("/");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  const ICON_MAP = { cake: "Cake", star: "Star", mail: "Mail", users: "Users" };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="whats-new-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-4 pt-5 pb-4 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 transition" data-testid="whats-new-back">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-slate-800">What's New</h1>
              {newCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-sky-500 text-white text-[10px] font-bold min-w-[20px] text-center" data-testid="new-count-badge">
                  {newCount}
                </span>
              )}
            </div>
            {lastUpdated && (
              <p className="text-[10px] text-slate-400 mt-0.5">
                Updated {formatDate(lastUpdated)}
              </p>
            )}
          </div>
          <button
            onClick={() => fetchFeed(true)}
            disabled={refreshing}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition disabled:opacity-50"
            data-testid="whats-new-refresh"
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Feed */}
      <div className="px-4 pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
          </div>
        ) : !feed || feed.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16" data-testid="whats-new-empty">
            <div className="w-14 h-14 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-slate-300" />
            </div>
            <p className="text-slate-700 font-semibold">Check back soon</p>
            <p className="text-slate-400 text-sm mt-1">We mine new cities every night - fresh deals are coming.</p>
          </motion.div>
        ) : (
          <div className="relative" ref={scrollRef}>
            {/* Timeline line */}
            <div className="absolute left-[11px] top-4 bottom-4 w-px bg-slate-200" />

            <AnimatePresence>
              {feed.map((item, i) => {
                const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.new_businesses;
                const Icon = cfg.icon;
                const isExpired = item.days_remaining <= 0;
                return (
                  <motion.div
                    key={`${item.type}-${i}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: isExpired ? 0.5 : 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    onClick={() => handleTap(item.action)}
                    className={`relative flex gap-3 mb-1 cursor-pointer group ${isExpired ? "pointer-events-none" : ""}`}
                    data-testid={`whats-new-item-${i}`}
                  >
                    {/* Dot + new indicator */}
                    <div className="flex flex-col items-center z-10 pt-3.5">
                      <div className={`w-[9px] h-[9px] rounded-full ${cfg.dot} ring-2 ring-white`} />
                    </div>

                    {/* Card */}
                    <div className={`flex-1 rounded-xl border p-3.5 mb-2 transition-all group-hover:shadow-md ${cfg.cardBg} ${cfg.cardBorder} ${cfg.hoverBorder} group-active:${cfg.activeBg}`}>
                      <div className="flex items-start gap-2.5">
                        <div className={`w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0 ${cfg.iconColor}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <NewDot isNew={item.is_new} />
                            <p className="text-[13px] font-bold text-slate-800 leading-tight">{item.headline}</p>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{item.subline}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-0.5">
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">{item.time_ago}</span>
                          <CountdownBadge days={item.days_remaining} isExpiring={item.is_expiring} />
                        </div>
                      </div>
                      {/* Tap indicator */}
                      {item.action && !isExpired && (
                        <div className="flex items-center justify-end mt-2 pt-1.5 border-t border-slate-100/60">
                          <span className="text-[10px] text-slate-400 font-medium flex items-center gap-0.5">
                            View <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Coming Soon Section */}
        <div className="mt-6 mb-4">
          <button
            onClick={fetchComingSoon}
            disabled={csLoading}
            className="w-full flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl hover:from-indigo-100 hover:to-purple-100 transition"
            data-testid="coming-soon-toggle"
          >
            <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-indigo-500" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-indigo-700">Coming Soon</p>
              <p className="text-[10px] text-indigo-400">Get notified when new features launch</p>
            </div>
            {csLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
            ) : (
              <ChevronDown className={`w-4 h-4 text-indigo-300 transition-transform ${showComingSoon ? "rotate-180" : ""}`} />
            )}
          </button>

          <AnimatePresence>
            {showComingSoon && comingSoon.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-2 mt-3">
                  {comingSoon.map((f) => (
                    <div
                      key={f.feature_id}
                      className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-100 shadow-sm"
                      data-testid={`coming-soon-${f.feature_id}`}
                    >
                      <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-700">{f.title}</p>
                        <p className="text-[10px] text-slate-400 leading-relaxed mt-0.5">{f.description}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleOptIn(f.feature_id, f.opted_in); }}
                        className={`p-2 rounded-xl transition flex-shrink-0 ${
                          f.opted_in
                            ? "bg-indigo-100 text-indigo-600"
                            : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                        }`}
                        data-testid={`opt-in-${f.feature_id}`}
                      >
                        {f.opted_in ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
